import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ChevronDown, ChevronUp, Filter } from "lucide-react";
import { getCurrentPage } from "~/functions";
import PageHeader from "~/components/PageHeader";
import Breadcrumbs from "~/components/Breadcrumbs";
import FiltersBar from "~/components/FiltersBar";
import PeriodFilter from "../../components/filters/periodFilter";
import PeriodByFilter from "../../components/filters/periodByFilter";
import ChartPayees from "../../components/charts/chartPayees";
import GridPayees from "../../components/grids/gridPayees";
import ChartOfAccountsModal from "./chartOfAccountsModal";
import api from "~/services/api";
import useReportsStore from "~/store/reportsSectionStore";
import { format } from "date-fns";
import { toast } from "react-toastify";

const reportOptions = ["Paid", "Pending", "Custom"];

export default function ReportFinancialPayees() {
  const { filial } = useSelector((state) => state.auth);
  const currentPage = getCurrentPage();
  const { filters, setFilters, chartOfAccountSelected } = useReportsStore();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [customStatus, setCustomStatus] = useState("paid");
  const [customStatusOpen, setCustomStatusOpen] = useState(false);
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const statusOptions = [
    { value: "paid", label: "Paid" },
    { value: "pending", label: "Pending" },
  ];

  useEffect(() => {
    if (!reportOptions.includes(filters.report)) {
      setFilters({ ...filters, report: "Paid" });
    }
  }, [filters.report, setFilters]);

  const loadData = () => {
    setLoading(true);
    const reportToFilter = {
      Paid: "paid",
      Pending: "pending",
    };

    const filterParam = reportToFilter[filters.report]
      ? `&filter=${reportToFilter[filters.report]}`
      : "";
    const baseQuery = `/reports/payees?period_from=${format(
      filters.period.from,
      "yyyy-MM-dd"
    )}&period_to=${format(filters.period.to, "yyyy-MM-dd")}&period_by=${
      filters.period_by.value
    }`;

    const query =
      filters.report === "Custom"
        ? `${baseQuery}&filter=${customStatus}`
        : `${baseQuery}${filterParam}`;

    // Se houver chart_codes selecionados, usa POST com body
    const hasChartCodes = selectedAccounts && selectedAccounts.length > 0;
    const chartCodes = hasChartCodes ? selectedAccounts.map(acc => acc.code) : [];

    const requestPromise = hasChartCodes
      ? api.post(query, { chart_codes: chartCodes })
      : api.get(query);

    requestPromise
      .then(({ data }) => {
        setData(data);
        setLoading(false);
      })
      .catch((err) => {
        toast(err?.response?.data?.error || "Failed to load payees data", {
          type: "error",
          autoClose: 3000,
        });
        setLoading(false);
      });
  };

  useEffect(() => {
    loadData();
  }, [filters, filial?.id, selectedAccounts]);

  useEffect(() => {
    if (filters.report === "Custom") {
      loadData();
    }
  }, [customStatus]);

  useEffect(() => {
    if (chartOfAccountSelected) {
      setTimeout(() => {
        document.getElementById("reportPage").scrollTo(0, 500);
      }, 300);
    }
  }, [chartOfAccountSelected]);

  useEffect(() => {
    if (filters.report === "Custom" && !customStatus) {
      setCustomStatus("paid");
    }
  }, [filters.report, customStatus]);

  return (
    <div
      id="reportPage"
      className="scroll-smooth h-full bg-white flex flex-1 flex-col justify-start items-start rounded-tr-2xl px-4 overflow-y-scroll"
    >
      <PageHeader>
        <Breadcrumbs currentPage={currentPage} />
        <FiltersBar></FiltersBar>
      </PageHeader>

      <div className="w-full flex flex-row justify-start items-start gap-4">
        <div className="flex flex-col h-full px-2 min-w-44 w-44 items-center justify-start text-xs gap-4 bg-gray-100">
          <div className="text-sm p-2 py-4">
            <strong>Payees Reports</strong>
          </div>
          {reportOptions.map((report, index) => (
            <button
              key={index}
              onClick={() =>
                setFilters({
                  ...filters,
                  report,
                  ...(report === "Custom"
                    ? { period: { ...filters.period, label: "Custom" } }
                    : {}),
                })
              }
              className={`text-sm w-full p-2 ${
                filters.report === report && "bg-zinc-200"
              } rounded hover:bg-zinc-300`}
            >
              {report}
            </button>
          ))}
        </div>
        <div className="flex flex-col w-full flex-1 items-center justify-center text-xs">
          <div className="flex w-full flex-row justify-start items-center rounded-tr-2xl p-2 gap-4">
            {!loading && (
              <>
                {filters.report === "Custom" ? (
                  <>
                    <PeriodFilter customOnly />
                    <PeriodByFilter />
                    {customStatusOpen && (
                      <div
                        onClick={() => setCustomStatusOpen(false)}
                        className="fixed top-0 left-0 w-full h-full bg-transparent z-10"
                      ></div>
                    )}
                    <div className={`relative z-20 flex flex-col gap-y-2 text-xs bg-zinc-100 rounded p-1 min-h-12 min-w-44 border ${customStatusOpen ? "border-primary shadow-lg" : "border-transparent"}`}>
                      <button
                        type="button"
                        onClick={() => setCustomStatusOpen(!customStatusOpen)}
                        className="flex flex-row justify-between items-center gap-x-2 text-xs cursor-pointer p-1 text-left"
                      >
                        <div className="flex flex-col">
                          <span>Status</span>
                          <strong className="text-sm">
                            {statusOptions.find((o) => o.value === customStatus)?.label ||
                              "Paid"}
                          </strong>
                        </div>
                        {customStatusOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                      {customStatusOpen && (
                        <div className="absolute left-0 top-full mt-1 w-full bg-white border border-zinc-300 rounded shadow-lg max-h-64 overflow-auto">
                          {statusOptions.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              className="w-full text-left p-2 hover:bg-zinc-100"
                              onClick={() => {
                                setCustomStatus(opt.value);
                                setCustomStatusOpen(false);
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setChartModalOpen(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
                      title="Filter by Chart of Accounts"
                    >
                      <Filter size={16} />
                      <span>Chart of Accounts</span>
                      {selectedAccounts.length > 0 && (
                        <span className="bg-white text-orange-500 rounded-full px-2 py-0.5 text-xs font-semibold">
                          {selectedAccounts.length}
                        </span>
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <PeriodFilter />
                    <PeriodByFilter />
                  </>
                )}
              </>
            )}
          </div>

          <div className="flex w-full flex-1 flex-col justify-start items-start">
            <ChartPayees data={data} />
            <GridPayees
              data={data}
              selectedChartCodes={selectedAccounts.map(acc => acc.code)}
              customStatus={filters.report === "Custom" ? customStatus : null}
            />
          </div>

          {loading && (
            <div className="flex justify-center items-center h-screen absolute top-0 left-0 w-full bg-gray-500 bg-opacity-50 z-10">
              <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-gray-700" />
            </div>
          )}
        </div>
      </div>

      <ChartOfAccountsModal
        isOpen={chartModalOpen}
        onClose={() => setChartModalOpen(false)}
        selectedAccounts={selectedAccounts}
        onApply={(accounts) => {
          setSelectedAccounts(accounts);
          setChartModalOpen(false);
        }}
      />
    </div>
  );
}
